import { formatNumber } from "utils/helpers"

import {
  roundBy,
  precisionRound,
  KmToMiles,
  KmToKnot,
  MetresToFeet,
  MillimetersToInches,
  CelsiusToFahrenheit,
} from "./helpers"

const METRIC = "Metric"
const IMPERIAL = "Imperial"

function altUnitsFor(units) {
  return units === IMPERIAL ? METRIC : IMPERIAL
}

class Units {
  constructor() {
    this.curr = {
      temperature: METRIC,
      length: METRIC,
      speed: METRIC,
    }
    this.listeners = []
  }

  get temperature() {
    return this.curr.temperature
  }

  get length() {
    return this.curr.length
  }

  get speed() {
    return this.curr.speed
  }

  get current() {
    return this.curr
  }

  // eslint-disable-next-line class-methods-use-this
  set current(_) {
    // do nothing
  }

  change(changes) {
    this.curr = { ...this.curr, ...changes }
    this.listeners.forEach((l) => {
      try {
        l(changes)
      } catch (e) {
        console.error(e)
      }
    })
  }

  onChange(cb) {
    this.listeners.push(cb)
  }

  formatLength(val, { precision = 2, altUnits = false, withUnits = false } = {}) {
    let cval = val
    let units = this.length
    if (altUnits) units = altUnitsFor(units)

    if (units === IMPERIAL) {
      cval = MetresToFeet(val)
    }

    cval = precisionRound(cval, precision)

    return withUnits ? `${cval}${this.formatLengthUnits({ altUnits })}` : cval
  }

  formatLengthUnits({ altUnits = false } = {}) {
    let units = this.length
    if (altUnits) units = altUnitsFor(units)

    if (units === IMPERIAL) return "ft"
    return "m"
  }

  formatSnow(
    val,
    { precision = 1, precisionLimit = 1.8, altUnits = false, withUnits = false } = {},
  ) {
    let cval = val
    let units = this.length
    if (altUnits) units = altUnitsFor(units)

    if (units === IMPERIAL) {
      cval = MillimetersToInches(val * 10)
    }

    const k = val > precisionLimit ? 1 : 10 ** precision
    cval = Math.round(cval * k) / k

    return withUnits ? `${cval}${this.formatSnowUnits({ altUnits })}` : cval
  }

  formatSnowUnits({ altUnits = false } = {}) {
    let units = this.length
    if (altUnits) units = altUnitsFor(units)

    if (units === IMPERIAL) return "in"
    return "cm"
  }

  formatMm(val, { precision = 1, precisionLimit = 2, altUnits = false, withUnits = false } = {}) {
    let cval = val
    let units = this.length
    if (altUnits) units = altUnitsFor(units)

    if (units === IMPERIAL) {
      cval = MillimetersToInches(val)
    }

    let prec = precision
    if (cval > precisionLimit) prec = 0
    else if (cval < 0.1) prec += 1

    const k = 10 ** prec
    cval = Math.round(cval * k) / k

    return withUnits ? `${cval}${this.formatMmUnits({ altUnits })}` : cval
  }

  formatMmUnits({ altUnits = false } = {}) {
    let units = this.length
    if (altUnits) units = altUnitsFor(units)

    if (units === IMPERIAL) return "in"
    return "mm"
  }

  formatSwell(val) {
    let cval = val
    const units = this.length

    if (units === IMPERIAL) {
      cval = MetresToFeet(val)
      if (cval < 10) cval = roundBy(cval, 0.5)
    }

    return cval >= 10 ? Math.round(cval) : precisionRound(cval, 1)
  }

  formatSpeed(val, { withUnits = false, precision = 5 } = {}) {
    let cval = val
    const units = this.speed

    if (units === IMPERIAL) {
      cval = KmToMiles(val)
    } else if (units === "Knot") {
      cval = KmToKnot(val)
    }

    cval = roundBy(cval, precision)

    return withUnits ? `${cval}${this.formatSpeedUnits()}` : cval
  }

  formatSpeedUnits() {
    const units = this.speed

    if (units === IMPERIAL) return "mph"
    if (units === "Knot") return "KN"
    return "km/h"
  }

  formatTemp(val, { withUnits = false, precision = 0 } = {}) {
    let cval = this.temperature === IMPERIAL ? CelsiusToFahrenheit(val) : val
    cval = cval.toFixed(precision)
    return withUnits ? `${cval}${this.formatTempUnits()}` : cval
  }

  formatTempDiff(val, { withUnits = false, sign = true, precision = 0 } = {}) {
    let cval = this.temperature === IMPERIAL ? val * 1.8 : val
    cval = cval.toFixed(precision)
    if (sign) {
      if (cval > 0) cval = `+${cval}`
    } else {
      cval = Math.abs(cval)
    }
    return withUnits ? `${cval}${this.formatTempUnits()}` : cval
  }

  formatTempUnits() {
    return this.temperature === IMPERIAL ? "\u00B0F" : "\u00B0C"
  }

  formatLevel(val) {
    let cval = val

    if (this.length === IMPERIAL) {
      cval = roundBy(MetresToFeet(val), 100)
    } else {
      cval = roundBy(cval, 50)
    }

    return cval
  }

  formatDist(val, { withUnits = false } = {}) {
    let cval = val
    const units = this.length
    if (units === IMPERIAL) cval = KmToMiles(val)

    cval = formatNumber(cval, cval < 1 ? 1 : 0)

    return withUnits ? `${cval} ${this.formatDistUnits()}` : cval
  }

  formatDistUnits() {
    const units = this.length
    if (units === IMPERIAL) return "mi"
    return "km"
  }
}

export default Units
